/**
 * Las opciones con las que se carga Google Maps — **congeladas**.
 *
 * ── El fallo que esto arregla ──────────────────────────────────────────────
 *
 * Cambiar de idioma con un mapa en pantalla tumbaba la página entera:
 *
 *   Uncaught Error: Loader must not be called again with different options.
 *   {… "language":"ca" …} !== {… "language":"es" …}
 *
 * No es un fallo de la aplicación, es cómo funciona Google Maps: su script se
 * carga UNA vez por página, así que el `Loader` de `@googlemaps/js-api-loader`
 * es un singleton y **lanza** si alguien intenta crearlo con otras opciones. Le
 * pasábamos `language` del i18n, o sea que cada cambio de idioma era un intento
 * de recrearlo — y el error subía hasta el error boundary del router y dejaba
 * la pantalla en «Error general».
 *
 * ── Qué se hace ───────────────────────────────────────────────────────────
 *
 * Se recuerda el idioma con el que se cargó el mapa la PRIMERA vez y se usa
 * siempre ese. Consecuencia asumida y sin remedio posible: los rótulos del mapa
 * (nombres de calles, controles) se quedan en ese idioma hasta que se recarga
 * la aplicación. El resto de la interfaz cambia al momento, como siempre.
 *
 * La alternativa sería forzar una recarga completa al cambiar de idioma, y eso
 * en una tablet de campo tira la caché de Apollo, el borrador del asistente y
 * la cola offline. No compensa por unos rótulos.
 *
 * ── Y por qué las opciones viven aquí ─────────────────────────────────────
 *
 * Porque hay DOS componentes que montan el `Wrapper` (`Map` y `MultiPathMap`) y
 * el singleton es uno solo: si algún día difieren en `region` o en `libraries`,
 * el error vuelve —esta vez al navegar de una pantalla a otra— y con la misma
 * cara. Compartiendo el objeto, no pueden divergir.
 */

/** AdvancedMarkerElement necesita la librería `marker`. */
export const MAP_LIBRARIES: 'marker'[] = ['marker'];

/** Región de sesgo de resultados. Se mantiene tal cual estaba. */
export const MAP_REGION = 'SP';

let idiomaDelMapa: string | null = null;

/**
 * El idioma con el que se cargó el mapa. La primera llamada con un idioma
 * válido manda; las siguientes lo ignoran.
 */
export const mapsLanguage = (currentLanguage?: string | null): string => {
  if (idiomaDelMapa === null && currentLanguage) {
    idiomaDelMapa = currentLanguage;
  }
  return idiomaDelMapa ?? currentLanguage ?? '';
};

/** Solo para los tests: olvida el idioma congelado. */
export const resetMapsLanguageForTests = (): void => {
  idiomaDelMapa = null;
};
